import moment from 'moment';
import { EmeraldError, EmeraldErrorModal } from 'src/common/api/emerald-error';
import { DataAddType } from 'src/common/enumerations/data-add-type';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { InvalidBarcode } from 'src/common/helpers/error-code';
import { IDataInformation } from 'src/common/interfaces/dtos/IDataInformation';
import { IMessageModal } from 'src/common/interfaces/dtos/IMessageModal';
import {
  ITransactionSearchState,
  TransactionSearchContext,
} from 'src/common/interfaces/states/ITransactionSearchState';
import { store } from 'src/common/redux/configure-store';
import { TransactionSearchActions } from 'src/common/redux/reducers/transaction-search-reducer';
import { searchForTransactions } from '../business-logic/service/search-for-transactions';
import {
  SearchTransactionsResponse,
  TlogTransactionType,
  TransactionSearchDto,
} from '../interfaces/transaction-search-response';

const transactionTypeMap = {
  [TransactionSearchContext.ReprintReceipt]: [
    TlogTransactionType.SALES,
    TlogTransactionType.RETURN,
    TlogTransactionType.TENDER_EXCHANGE,
  ],
  [TransactionSearchContext.TBR]: [TlogTransactionType.SALES, TlogTransactionType.RETURN],
};

export const searchTransactionByBarcode = async (
  decodedResult: IDataInformation,
  searchDto: TransactionSearchDto = {},
  searchContext: TransactionSearchContext,
): Promise<SearchTransactionsResponse> => {
  const transactionTypes = transactionTypeMap[searchContext];
  const searchParam = {
    transactionTypes,
    isTrainingMode: store.getState().posMode.trainingMode,
    ...searchDto,
    ...setSearchParam(decodedResult),
  };
  return searchForTransactions(searchParam);
};

const setSearchParam = (decodeResult: IDataInformation): TransactionSearchDto => {
  let setSearchParam: TransactionSearchDto;
  const transactionSearchState = store.getState().transactionSearch;

  switch (decodeResult.type) {
    case DataAddType.Transaction:
      if (
        decodeResult.issueDate &&
        moment(decodeResult.issueDate).isAfter(transactionSearchState.dateRange.startDate)
      ) {
        setSearchParam = {
          siteInfoIds: decodeResult.storeId ? [decodeResult.storeId] : undefined,
          touchPointId: decodeResult.touchPointId,
          transactionNumber: decodeResult.id,
          fromTransactionDateTimeUtc: {
            dateTime: transactionSearchState.dateRange.startDate.toISOString(),
            originalOffset: '',
          },
          toTransactionDateTimeUtc: {
            dateTime: transactionSearchState.dateRange.endDate.toISOString(),
            originalOffset: '',
          },
        };
      } else {
        const oldTransactionErrorModal: IMessageModal = {
          type: EmeraldDialogTypes.Error,
          message: 'TBRItemCannotBeReturned',
          subMessage: 'TBRItemCannotBeReturnedSubMessage',
          acceptButtonLabel: 'OK',
          hideCancelButton: true,
        };
        throw new EmeraldErrorModal(oldTransactionErrorModal);
      }

      break;

    case DataAddType.Customer:
      setSearchParam = {
        loyaltyCard: decodeResult.id,
        fromTransactionDateTimeUtc: {
          dateTime: transactionSearchState.dateRange.startDate.toISOString(),
          originalOffset: '',
        },
        toTransactionDateTimeUtc: {
          dateTime: transactionSearchState.dateRange.endDate.toISOString(),
          originalOffset: '',
        },
        pageNumber: store.getState().transactionSearch.pageNumber,
        pageSize: store.getState().transactionSearch.pageSize,
      };
      store.dispatch(
        TransactionSearchActions.setSearchParam({
          loyaltyCard: decodeResult.id,
        }),
      );

      break;

    default:
      throw new EmeraldError(InvalidBarcode);
  }
  return setSearchParam;
};

export const searchTransactionByParam = async (
  searchParams: ITransactionSearchState,
  searchContext: TransactionSearchContext,
): Promise<SearchTransactionsResponse> => {
  // extract unused isSearchResultView and transactionList out of search DTO
  const transactionTypes = transactionTypeMap[searchContext];
  const {
    isSearchResultView,
    transactionList,
    dateRange,
    storeNumber,
    posNumber,
    itemCode,
    ...restOfParam
  } = searchParams;

  const transactionParams: TransactionSearchDto = {
    fromTransactionDateTimeUtc: {
      dateTime: dateRange.startDate.toISOString(),
      originalOffset: '',
    },
    toTransactionDateTimeUtc: {
      dateTime: dateRange.endDate.toISOString(),
      originalOffset: '',
    },
    siteInfoIds: storeNumber ? [storeNumber] : undefined,
    touchPointId: posNumber,
    itemProductIds: itemCode ? [itemCode] : undefined,
    isTrainingMode: store.getState().posMode.trainingMode,
    transactionTypes,
    ...restOfParam,
  };
  return searchForTransactions(transactionParams);
};
